import {
  ComponentClass,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import classnames from "classnames";
import moment from "moment-timezone";
import { default as DatePicker, ReactDatePickerProps } from "react-datepicker";
import MaskedInput from "react-text-mask";
import { convertFromServerTime, convertToServerTime } from "../../../../utils";
import { AppConfigContext } from "../../../generic";
import { Input, InputProps } from "../Input";
import CalendarButton from "./CalendarButton";
import DateFieldMask from "./DateFieldMask";

import { ReactComponent as ArrowLeft } from "./images/simple-arrow-left.svg";
import { ReactComponent as ArrowRight } from "./images/simple-arrow-right.svg";

import "react-datepicker/dist/react-datepicker.css";
import styles from "./style.module.scss";

export const defaultTimeValueStartOfDay = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};

export const defaultTimeValueEndOfDay = {
  hour: 23,
  minute: 59,
  second: 59,
  millisecond: 999,
};


export type DateFieldProps = Omit<InputProps<ReactDatePickerProps, DatePicker>, "component" | "onChange" | "value"> & {
  onChange?: (date: string | null) => void,
  value?: string | null,

  timeZone?: string,
  keepTimeValue?: boolean,
  defaultTimeValue?: {
    hour?: number,
    minute?: number,
    second?: number,
    millisecond?: number,
  }
  serverDateTimeFormat?: string,
  defaultServerDateTimeFormatMode?: "date" | "datetime",
}

export const DateField = memo(forwardRef<DatePicker, DateFieldProps>((
  {
    onBlur,
    onChange,
    onClear,
    dateFormat,
    timeZone,
    isClearable = false,
    keepTimeValue = false,
    defaultTimeValue,
    disabled,
    serverDateTimeFormat,
    defaultServerDateTimeFormatMode = "date",
    ...props
  }, ref) => {
  const appConfig = useContext(AppConfigContext);
  const [ open, setOpen ] = useState(false);
  const inputRef = useRef<MaskedInput | undefined>(undefined);
  const close = useCallback(() => {
    flushSync(() => {
      setOpen(false);
    });

    inputRef.current?.inputElement?.focus?.();
  }, [ setOpen ]);

  const defaultServerDateTimeFormat = useMemo(() => {
    switch (defaultServerDateTimeFormatMode) {
      case "datetime":
        return appConfig.serverDateTimeFormatMoment;
      case "date":
      default:
        return appConfig.serverDateFormatMoment;
    }
  }, [ appConfig.serverDateFormatMoment, appConfig.serverDateTimeFormatMoment, defaultServerDateTimeFormatMode ]);

  const containerProps = useMemo(() => ({
    onBlur: (e: FocusEvent<HTMLInputElement>) => {
      if (
        open &&
        e.relatedTarget &&
        !e.currentTarget.contains(e.relatedTarget as Node) &&
        !(e.relatedTarget as HTMLElement).className.includes("react-datepicker")
      ) {
        close();
      }
      onBlur?.(e);
    },
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape" && open) {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    },
  }), [ open, close, onBlur ]);
  const wrappedOnBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    close();
  }, [ close, onBlur ]);
  const wrappedOnClear = useCallback(() => onChange?.(null), [ onChange ]);
  const wrappedOnChange = useCallback((value: Date) => {
    if (value) {
      const realTimeZone = timeZone || appConfig.timeZone;

      const dateParts: {
        year?: number,
        month?: number,
        date?: number,
        hour?: number,
        minute?: number,
        second?: number,
        millisecond?: number,
      } = {
        year: value?.getFullYear?.(),
        month: value?.getMonth?.(),
        date: value?.getDate?.(),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        ...defaultTimeValue,
      };

      if (keepTimeValue && props.value) {
        const existingMoment = convertFromServerTime(props.value, appConfig.serverTimeZone, defaultServerDateTimeFormat)
          .tz(realTimeZone);

        if (existingMoment.isValid()) {
          dateParts.hour = existingMoment.hour();
          dateParts.minute = existingMoment.minute();
          dateParts.second = existingMoment.second();
          dateParts.millisecond = existingMoment.millisecond();
        }
      }

      const momentDate = moment.tz(dateParts, realTimeZone);
      const date = momentDate?.isValid?.()
        ? convertToServerTime(momentDate, appConfig.serverTimeZone, serverDateTimeFormat || defaultServerDateTimeFormat) || undefined
        : undefined;
      return onChange?.(date || null);
    }
    return onChange?.(null);
  }, [ onChange, timeZone, serverDateTimeFormat, defaultServerDateTimeFormat, appConfig.timeZone, keepTimeValue, props.value, appConfig.serverTimeZone, defaultTimeValue ]);

  const additionalOnBlurCheck = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget) {
      if ((e.relatedTarget as HTMLElement).className.includes("react-datepicker")) {
        return false;
      }
    }
    if (e.target) {
      if (e.target.tagName.toLowerCase() !== "input" && e.target.className.includes("react-datepicker")) {
        return false;
      }
    }
    return !e.defaultPrevented;
  }, []);

  const dateFormatTemp = "YYYY-MM-DDTHH:mm:ss.SSSS";
  const convertFromServerTime1 = convertFromServerTime(props.value || "", appConfig.serverTimeZone, defaultServerDateTimeFormat);
  const format = convertFromServerTime1.format(dateFormatTemp);
  const tzTime = moment.tz(
    format,
    dateFormatTemp,
    appConfig.serverTimeZone,
  )
    .tz(
      timeZone || appConfig.timeZone,
    );
  const selectedDate = props.value ? new Date(tzTime.format(dateFormatTemp)) : null;

  return (
    <Input
      component={DatePicker as ComponentClass<ReactDatePickerProps>}
      popperClassName={styles.popper}
      popperPlacement="bottom-start"
      placeholderText={`${appConfig.dateFormat}`.toLocaleLowerCase()}
      customInput={<DateFieldMask additionalRef={inputRef} />}
      formatWeekDay={(v: any) => v?.substring?.(0, 3)}
      dateFormat={dateFormat || appConfig.dateFormat}
      focused={open}
      disabled={disabled}
      ref={ref}
      open={open}
      enableTabLoop={false}
      showPopperArrow={false}
      disabledKeyboardNavigation
      onBlur={wrappedOnBlur}
      onCalendarClose={close}
      onChange={wrappedOnChange}
      onSelect={close}
      selected={selectedDate}
      openToDate={selectedDate || undefined}
      onClickOutside={close}
      containerProps={containerProps}
      onErrorClick={() => setOpen(!open)}
      portalId="popperPortal"
      additionalOnBlurCheck={additionalOnBlurCheck}
      endAdornment={
        <CalendarButton
          disabled={disabled}
          onClick={() => setOpen(!open)}
          onBlur={(e: FocusEvent) => e.preventDefault()} />
      }
      previousMonthButtonLabel={<ArrowLeft width={9} height={15} />}
      nextMonthButtonLabel={<ArrowRight width={9} height={15} />}
      onClear={wrappedOnClear}
      isClearable={isClearable}
      {...props}
      className={classnames(styles.root, props.className)}
      value={undefined}
    />
  );
}));

export default DateField;
